// =====================================================================
// RdvDocsZeus.jsx — Commandes prêtes v3 B (16-10-2026) : rendez-vous + documents à imprimer, côté ZEUS
// =====================================================================
//   <RdvDocsCommande orderId />  écran de la commande : pastille, rendez-vous (et précédents), documents
//   <RdvDocsAFaire />            liste « à traiter » toutes commandes (rendez-vous pas encore vus,
//                                documents à imprimer) — pour la vue d'ensemble des commandes prêtes
// Documents : imprimante PROPOSÉE (règle du serveur, READY_PRINT_RULES) ; « Imprimer » ouvre le fichier
// dans un nouvel onglet (impression Windows : choisir cette imprimante et le nombre de copies) ; un ZPL
// part au pont d'impression local (http://127.0.0.1:9110, Imprimante-Bridge.ps1), sinon il est
// téléchargé. « Marquer imprimé » est un geste séparé (le document sort de la pastille).
// Droits (SPEC v3 D, 17/10) : tout le monde dans le module VOIT (sans le droit d'imprimer : « 📄 Ouvrir » un
// PDF / une image) ; « ✓ Vu », « 🖨 Imprimer » et « Marquer imprimé » (et l'annuler) = droit « Imprimer les
// documents » — prop `imprimer` (faux par défaut), passée par la page ; le serveur refuse de toute façon
// (403 « Droit manquant »).
// v3 C (17-10-2026) « 🖨 Imprimer tout » (SPEC v3 C §1) : UN bouton envoie tous les documents reçus à l'agent de
// server-home, chacun sur sa bonne imprimante (Zebra quai / Konica), avec ses copies ; état par document (En file… /
// Imprimé ✓ — imprimante / Échec : raison — ↻ Réessayer), relu toutes les 3 s tant qu'il reste des travaux ;
// « ↻ Réimprimer » par document. Agent indisponible : le bouton le dit AVANT le clic ; on revient alors au mode
// de secours d'avant (« 🖨 Imprimer » = ouvrir / pont local, « Marquer imprimé ») — sinon un document imprimé à la
// main resterait « à imprimer » et repartirait au prochain « Imprimer tout ». « 📄 Ouvrir » reste toujours.
// 21-10-2026 (décision Silviu) : la liste « à traiter » montre UNE ligne par commande avec UN bouton « 🖨 Imprimer »
// (tous ses documents, chacun sur son imprimante), relue toutes les 15 s ; les cartes par document (Ouvrir,
// ↻ Réessayer, Marquer imprimé) restent sur l'écran de la commande.
// =====================================================================

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { readyRdvDocs, errMsg } from '../../services/api.js';
import BadgeTest from './BadgeTest.jsx';
import { dateHeureBxl } from './dates.js';
import {
  jourCourt, texteRdv, texteEtatMail, texteEtatConfirmation, envoyerAuPont, apresEchecPont,
  dispoImpression, travailActif, lotsImprimerTout, boutonImprimerTout, texteLancement, etatImpressionDoc, texteImprime, libelleCible,
  texteAFaire, lignesParCommande, texteResumeCommande, etatLigneCommande, boutonLigneCommande, texteFinCommande,
} from './rdvDocs.js';
import { LIBELLES_DROITS, DROIT_IMPRIMER } from './droits.js';

// Sans le droit « Imprimer les documents » : les boutons laissent la place à cette note.
const NOTE_SANS_IMPRIMER = `« Vu » et impression : droit « ${LIBELLES_DROITS[DROIT_IMPRIMER]} »`;

function telecharger(octets, nom) {
  const url = URL.createObjectURL(new Blob([octets], { type: 'application/octet-stream' }));
  const a = document.createElement('a');
  a.href = url; a.download = nom || 'etiquette.zpl';
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

function CarteRdv({ rdv, commande, onMaj, setMsg, imprimer = false }) {
  const [enCours, setEnCours] = useState(false);
  async function vu() {
    setEnCours(true);
    try { await readyRdvDocs.vu(rdv.id); await onMaj(); } catch (e) { setMsg({ err: true, texte: `« Vu » impossible : ${errMsg(e)}` }); } finally { setEnCours(false); }
  }
  return (
    <div className="rdz-carte" id={`rdv-${rdv.id}`}>
      {commande && <ReferenceCommande commande={commande} />}
      <div><b>📅 {texteRdv(rdv, 'fr')}</b>{!rdv.vuLe && <span className="rdz-nouveau">nouveau</span>}</div>
      <div className="rdz-note">
        {[rdv.societe, rdv.nom, rdv.email, rdv.tel, rdv.plaque ? `plaque ${rdv.plaque}` : null].filter(Boolean).join(' · ')}
      </div>
      <div className="rdz-note">{`Pris le ${dateHeureBxl(rdv.createdAt)} · équipe : ${texteEtatMail(rdv.mailState)} · confirmation : ${texteEtatConfirmation(rdv.confirmState)}`}</div>
      {rdv.mailState === 'ECHEC' && <div className="rdz-note err">{`Équipe : ${rdv.mailError || 'échec'}`}</div>}
      {rdv.confirmState === 'ECHEC' && <div className="rdz-note err">{`Confirmation : ${rdv.confirmError || 'échec'}`}</div>}
      {rdv.vuLe
        ? <div className="rdz-note">{`Vu par ${rdv.vuParNom || '—'} le ${dateHeureBxl(rdv.vuLe)}`}</div>
        : imprimer
          ? <button type="button" className="rdz-btn" disabled={enCours} onClick={vu}>✓ Vu</button>
          : <div className="rdz-note">{`Pas encore vu (${NOTE_SANS_IMPRIMER}).`}</div>}
    </div>
  );
}

function ReferenceCommande({ commande }) {
  return (
    <div className="rdz-ref">
      <Link to={`/commande-prete/${commande.id}`}>{commande.orderRef}</Link>
      {commande.client && <span className="rdz-note"> · {commande.client.name}</span>}
      {commande.isTest && <BadgeTest style={{ marginLeft: 6 }} />}
    </div>
  );
}

function CarteDocument({ d, avecCommande, onMaj, setMsg, imprimer = false, auto = null }) {
  const [enCours, setEnCours] = useState(false);
  const dispo = dispoImpression(auto).disponible;
  const etat = etatImpressionDoc(d, dispo);
  // Impression à la main (comme avant) : agent indisponible, ou dernier essai en ÉCHEC (l'agent refuse certains
  // documents : il faut une issue, et pouvoir « Marquer imprimé » ce qu'on a imprimé à la main).
  const secours = imprimer && (!dispo || etat.action === 'reessayer');
  async function reimprimer() {
    setEnCours(true);
    try { await readyRdvDocs.reimprimer(d.id); await onMaj(); } catch (e) { setMsg({ err: true, texte: `Impression impossible : ${errMsg(e)}` }); } finally { setEnCours(false); }
  }
  async function imprimerZpl() {
    setEnCours(true);
    try {
      const octets = await readyRdvDocs.octetsZpl(d.id);
      try {
        const n = await envoyerAuPont(octets, d.copies);
        setMsg({ texte: `${d.nom} : ${n} copie${n > 1 ? 's' : ''} envoyée${n > 1 ? 's' : ''} à la Zebra du pont d'impression de ce poste. Vérifiez l'impression puis « Marquer imprimé ».` });
      } catch (e) {
        const suite = apresEchecPont(e);
        if (suite.telecharger) telecharger(octets, d.nom);
        setMsg({ warn: true, texte: suite.texte });
      }
    } catch (e) { setMsg({ err: true, texte: `Fichier illisible : ${errMsg(e)}` }); } finally { setEnCours(false); }
  }
  async function marquer(imprime) {
    setEnCours(true);
    try { await readyRdvDocs.imprime(d.id, imprime); await onMaj(); } catch (e) { setMsg({ err: true, texte: errMsg(e) }); } finally { setEnCours(false); }
  }
  const imprime = d.statut === 'IMPRIME';
  return (
    <div className={`rdz-carte${imprime ? ' fait' : ''}`} id={`doc-${d.id}`}>
      {avecCommande && d.commande && <ReferenceCommande commande={d.commande} />}
      <div><b>{d.typeLibelle}</b> — <span className="rdz-mono">{d.nom}</span> <span className="rdz-note">({d.format})</span></div>
      <div className="rdz-imprimante">{`🖨 ${d.cible ? libelleCible(d.cible) : d.imprimante} · ${d.copies} copie${d.copies > 1 ? 's' : ''}`}</div>
      {secours && d.format === 'ZPL' && <div className="rdz-note">ZPL : part à la Zebra du pont d'impression de ce poste (Demarrer-impression.bat).</div>}
      <div className="rdz-note">
        {[`Transporteur : ${d.transporteur || '—'}`, d.dateEnlevement ? `enlèvement souhaité ${jourCourt(d.dateEnlevement, 'fr')}` : null, `reçu le ${dateHeureBxl(d.recuLe)}`]
          .filter(Boolean).join(' · ')}
      </div>
      {d.instructions && <div className="rdz-note rdz-instr">{`Instructions : ${d.instructions}`}</div>}
      {d.mailState === 'ECHEC' && <div className="rdz-note err">{`Mail à l'équipe non parti : ${d.mailError || ''}`}</div>}
      {etat.texte && <div className={`rdz-etat ${etat.classe}`} role="status">{etat.texte}</div>}
      <div className="rdz-actions">
        {secours && (d.format === 'ZPL'
          ? <button type="button" className="rdz-btn" disabled={enCours} onClick={imprimerZpl}>🖨 Imprimer</button>
          : <a className="rdz-btn" href={d.url} target="_blank" rel="noopener noreferrer">🖨 Imprimer</a>)}
        {/* « Ouvrir » reste (impression manuelle de secours), avec ou sans le droit d'imprimer (« Voir » : documents
            reçus) ; un ZPL n'est qu'une étiquette pour la Zebra, rien à lire. */}
        {!secours && d.format !== 'ZPL' && <a className="rdz-btn" href={d.url} target="_blank" rel="noopener noreferrer">📄 Ouvrir</a>}
        {imprimer && dispo && etat.action && <button type="button" className="rdz-btn" disabled={enCours} onClick={reimprimer}>{etat.action === 'reessayer' ? '↻ Réessayer' : '↻ Réimprimer'}</button>}
        {imprime ? (
          <>
            <span className="rdz-note ok">{`${texteImprime(d)} · par ${d.imprimeParNom || '—'} le ${dateHeureBxl(d.imprimeLe)}`}</span>
            {secours && <button type="button" className="rdz-btn discret" disabled={enCours} onClick={() => marquer(false)}>Annuler</button>}
          </>
        ) : secours
          ? <button type="button" className="rdz-btn primaire" disabled={enCours || d.impression?.statut === 'EN_COURS'} onClick={() => marquer(true)}>✓ Marquer imprimé</button>
          : !imprimer && !etat.texte && <span className="rdz-note">{`À imprimer (${NOTE_SANS_IMPRIMER}).`}</span>}
      </div>
    </div>
  );
}

/**
 * État de l'impression automatique : celui de la dernière lecture des documents, relu toutes les 20 s tant que
 * l'écran montre des documents (`actif`) — un agent qui s'arrête est dit AVANT le clic, pas après.
 */
function useImpressionAuto(depuisLecture, actif) {
  const [auto, setAuto] = useState(null);
  useEffect(() => { if (depuisLecture) setAuto(depuisLecture); }, [depuisLecture]);
  useEffect(() => {
    if (!actif) return undefined;
    const minuteur = setInterval(() => { readyRdvDocs.etatImpression().then(setAuto).catch(() => {}); }, 20_000);
    return () => clearInterval(minuteur);
  }, [actif]);
  return auto;
}

/** « 🖨 Imprimer tout (N) » : les documents montrés, groupés par commande ; rien si rien à imprimer. */
function ImprimerTout({ documents, auto, onMaj, setMsg }) {
  const [enCours, setEnCours] = useState(false);
  // « En cours… » jusqu'à l'arrivée des documents RELUS après le clic (jamais un 2e clic sur l'ancienne liste) ;
  // relecture en échec : rendu au bout de 10 s.
  const auClic = useRef(null);
  useEffect(() => { if (auClic.current && auClic.current !== documents) { auClic.current = null; setEnCours(false); } }, [documents]);
  const b = boutonImprimerTout({ documents, auto, enCours });
  if (!b) return null;
  async function lancer() {
    setEnCours(true);
    auClic.current = documents;
    let n = 0;
    try {
      for (const [orderId, ids] of lotsImprimerTout(documents)) n += (await readyRdvDocs.imprimerTout(orderId, ids)).crees || 0;
      setMsg({ texte: texteLancement(n) });
    } catch (e) {
      setMsg({ err: true, texte: `${n ? `${texteLancement(n)} Puis : ` : ''}Impression impossible : ${errMsg(e)}` });
    } finally {
      setTimeout(() => { if (auClic.current) { auClic.current = null; setEnCours(false); } }, 10_000);
      await onMaj();
    }
  }
  return (
    <div className="rdz-tout">
      <button type="button" className="rdz-btn primaire" id="imprimerTout" disabled={b.desactive} onClick={lancer}>{b.texte}</button>
      {b.note && <span className="rdz-note">{b.note}</span>}
    </div>
  );
}

/**
 * Une commande de la liste « à traiter » (décision Silviu 21/10) : référence + client, combien de documents partent sur
 * quelle imprimante, et UN bouton « 🖨 Imprimer » — tous ses documents à l'agent, chacun sur son imprimante.
 */
function LigneCommande({ l, auto, envoi, onEnvoi, onMaj, setMsg, imprimer = false }) {
  const [enCours, setEnCours] = useState(false);
  const b = imprimer ? boutonLigneCommande(l, { auto, enCours }) : null;
  const etat = etatLigneCommande(l, { disponible: dispoImpression(auto).disponible, envoi });
  const ref = l.commande?.orderRef || '—';
  async function lancer() {
    setEnCours(true);
    try {
      const r = await readyRdvDocs.imprimerTout(l.orderId, l.aImprimer);
      if (r?.crees) onEnvoi({ n: r.crees, ids: (r.travaux || []).map((t) => t.documentId), ref });
      else setMsg({ texte: `${ref} : ${texteLancement(0)}` });
    } catch (e) {
      setMsg({ err: true, texte: `${ref} : impression impossible : ${errMsg(e)}` });
    } finally {
      await onMaj();          // la liste relue AVANT de rendre le bouton : jamais un 2e clic sur l'ancienne
      setEnCours(false);
    }
  }
  return (
    <div className="rdz-ligne" id={`cmd-${l.orderId}`}>
      <div className="rdz-ligne-texte">
        {l.commande && <ReferenceCommande commande={l.commande} />}
        <div className="rdz-note">
          {[texteResumeCommande(l.docs), l.recuLe ? `reçu le ${dateHeureBxl(l.recuLe)}` : null, l.enlevement ? `enlèvement souhaité ${jourCourt(l.enlevement, 'fr')}` : null]
            .filter(Boolean).join(' · ')}
        </div>
        {l.instructions && <div className="rdz-note rdz-instr">{`Instructions : ${l.instructions}`}</div>}
        {etat && <div className={`rdz-etat ${etat.classe}`} role="status">{etat.texte}</div>}
        {b?.note && <div className="rdz-note">{b.note} — <Link to={`/commande-prete/${l.orderId}`}>imprimer depuis la commande</Link></div>}
        {!imprimer && <div className="rdz-note">{`À imprimer (${NOTE_SANS_IMPRIMER}).`}</div>}
      </div>
      {b && <button type="button" className="rdz-btn primaire" disabled={b.desactive} onClick={lancer}>{b.texte}</button>}
    </div>
  );
}

function Message({ msg }) {
  if (!msg) return null;
  return <div className={`rdz-msg${msg.err ? ' err' : msg.warn ? ' warn' : ''}`} role="status">{msg.texte}</div>;
}

/** Écran de la commande : rien tant qu'il n'y a ni rendez-vous ni document. `imprimer` : droit « Imprimer les documents ». */
export function RdvDocsCommande({ orderId, imprimer = false }) {
  const [data, setData] = useState(null);
  const [msg, setMsg] = useState(null);
  const [rev, setRev] = useState(0);
  // Autre commande : rien de l'ancienne ne reste affiché (ni ses documents, ni son message)…
  useEffect(() => { setData(null); setMsg(null); }, [orderId]);
  // … et une réponse arrivée APRÈS le changement (ou le départ de l'écran) est ignorée.
  useEffect(() => {
    let actif = true;
    readyRdvDocs.commande(orderId).then((d) => {
      if (!actif) return;
      setData({ ...d, pour: orderId });
      setMsg((m) => (m?.lecture ? null : m));   // une erreur de lecture d'avant ne reste pas sur des données à jour
    }).catch((e) => {
      if (!actif) return;
      if (e?.response?.status === 404) setData(null);
      else setMsg({ err: true, lecture: true, texte: `Rendez-vous et documents illisibles : ${errMsg(e)}` });
    });
    return () => { actif = false; };
  }, [orderId, rev]);
  const charger = useCallback(async () => { setRev((x) => x + 1); }, []);
  const documents = data && data.pour === orderId ? data.documents : [];
  const auto = useImpressionAuto(data?.impressionAuto, imprimer && documents.length > 0);
  // Travaux en file ou en cours : relu toutes les 3 s jusqu'à ce qu'ils soient finis.
  const actifs = documents.some(travailActif);
  useEffect(() => {
    if (!actifs) return undefined;
    const minuteur = setTimeout(() => setRev((x) => x + 1), 3000);   // après CHAQUE réponse (data / msg) : jamais une lecture lente jetée
    return () => clearTimeout(minuteur);
  }, [actifs, data, msg]);
  if (!data || data.pour !== orderId || (!data.rdv && !data.historique.length && !data.documents.length)) return msg ? <section className="rdz"><style>{CSS}</style><Message msg={msg} /></section> : null;
  return (
    <section className="rdz" id="rdvDocs" aria-label="Rendez-vous et documents">
      <style>{CSS}</style>
      <div className="rdz-tete">
        <b>Rendez-vous & documents</b>
        {data.aVoir > 0 && <span className="rdz-pastille" title="Rendez-vous pas encore vu + documents à imprimer">{data.aVoir}</span>}
      </div>
      {data.rdv && <CarteRdv rdv={data.rdv} onMaj={charger} setMsg={setMsg} imprimer={imprimer} />}
      {data.historique.length > 0 && (
        <details className="rdz-histo">
          <summary>{`Rendez-vous précédents (${data.historique.length})`}</summary>
          {data.historique.map((r) => <div key={r.id} className="rdz-note">{`${texteRdv(r, 'fr')} — ${r.societe} · pris le ${dateHeureBxl(r.createdAt)} (${r.remplace ? 'remplacé' : 'enlèvement précédent'})`}</div>)}
        </details>
      )}
      {imprimer && <ImprimerTout documents={data.documents} auto={auto} onMaj={charger} setMsg={setMsg} />}
      {data.documents.map((d) => <CarteDocument key={d.id} d={d} onMaj={charger} setMsg={setMsg} imprimer={imprimer} auto={auto} />)}
      <Message msg={msg} />
    </section>
  );
}

/**
 * Liste « à traiter » de toutes les commandes (rien s'il n'y a rien). `imprimer` : droit « Imprimer les documents ».
 * 21/10 (décision Silviu) : UNE ligne par commande, UN bouton « 🖨 Imprimer » ; le détail reste sur la commande.
 */
export function RdvDocsAFaire({ imprimer = false }) {
  const [data, setData] = useState(null);
  const [msg, setMsg] = useState(null);
  const [termines, setTermines] = useState([]);   // lignes parties après « Imprimer » : « … imprimés ✓ » quelques secondes
  const envois = useRef(new Map());               // orderId → { n, ids, ref } noté au clic : progression, puis relecture
  // Lectures qui se chevauchent (15 s, 3 s, clic) : seule la DERNIÈRE lancée est appliquée — une réponse plus
  // ancienne arrivée après ferait croire qu'une commande en cours d'impression a quitté la liste.
  const derniere = useRef(0);
  const charger = useCallback(() => {
    const n = ++derniere.current;
    return readyRdvDocs.aTraiter()
      .then((d) => { if (n === derniere.current) { setData(d); setMsg((m) => (m?.lecture ? null : m)); } })
      .catch((e) => { if (n === derniere.current) setMsg({ err: true, lecture: true, texte: `Rendez-vous et documents illisibles : ${errMsg(e)}` }); });
  }, []);
  // Relu toutes les 15 s (décision Silviu 21/10) : des documents envoyés par le client pendant que la page est
  // ouverte y apparaissent d'eux-mêmes, au plus 15 s après.
  useEffect(() => {
    charger();
    const minuteur = setInterval(charger, 15_000);
    return () => clearInterval(minuteur);
  }, [charger]);
  const auto = useImpressionAuto(data?.impressionAuto, imprimer && !!data?.documents.length);
  // Travaux en file ou en cours : relu toutes les 3 s jusqu'à ce qu'ils soient finis.
  const actifs = !!data?.documents.some(travailActif);
  useEffect(() => {
    if (!actifs) return undefined;
    const minuteur = setTimeout(charger, 3000);   // après CHAQUE réponse (data / msg) : jamais deux lectures qui se doublent
    return () => clearTimeout(minuteur);
  }, [actifs, charger, data, msg]);
  const lignes = useMemo(() => lignesParCommande(data?.documents), [data]);
  // Commande envoyée qui a quitté la liste (documents passés « imprimé ») : relue sur son écran — jamais « ✓ » sans
  // cette relecture —, montrée quelques secondes, puis retirée. Restée avec un échec : sa ligne le dit elle-même.
  useEffect(() => {
    for (const [orderId, e] of envois.current) {
      const l = lignes.find((x) => x.orderId === orderId);
      if (l && (l.actifs || l.aImprimer.length)) continue;
      envois.current.delete(orderId);
      if (l) continue;
      setTermines((t) => [...t.filter((x) => x.orderId !== orderId), { orderId, texte: `${e.ref} : vérification de l'impression…`, ok: null }]);
      readyRdvDocs.commande(orderId)
        .then((c) => texteFinCommande(e.ref, e.ids, c?.documents))
        .catch(() => ({ texte: `${e.ref} : impression terminée — résultat non relu, voyez la commande`, ok: false }))
        .then((fin) => {
          setTermines((t) => [...t.filter((x) => x.orderId !== orderId), { orderId, ...fin }]);
          setTimeout(() => setTermines((t) => t.filter((x) => x.orderId !== orderId)), 8000);
        });
    }
  }, [lignes]);
  // (une commande envoyée encore notée : la boîte reste, pas de clignement avant « vérification… »)
  if (!data || (!data.rdv.length && !lignes.length && !termines.length && !envois.current.size)) return msg ? <section className="rdz"><style>{CSS}</style><Message msg={msg} /></section> : null;
  return (
    <section className="rdz" id="rdvDocsAFaire" aria-label="Rendez-vous et documents à traiter">
      <style>{CSS}</style>
      <div className="rdz-tete">
        <b>À traiter</b>
        {data.rdv.length + lignes.length > 0 && <span className="rdz-pastille">{data.rdv.length + lignes.length}</span>}
        <span className="rdz-note">{texteAFaire(data.rdv.length, lignes.length)}</span>
      </div>
      {data.rdv.map((r) => <CarteRdv key={r.id} rdv={r} commande={r.commande} onMaj={charger} setMsg={setMsg} imprimer={imprimer} />)}
      {lignes.map((l) => (
        <LigneCommande key={l.orderId} l={l} auto={auto} envoi={envois.current.get(l.orderId)}
          onEnvoi={(e) => envois.current.set(l.orderId, e)} onMaj={charger} setMsg={setMsg} imprimer={imprimer} />
      ))}
      {termines.map((t) => <div key={`fin-${t.orderId}`} className="rdz-ligne fait" role="status"><span className={`rdz-note${t.ok ? ' ok' : t.ok === false ? ' err' : ''}`}>{t.texte}</span></div>)}
      <Message msg={msg} />
    </section>
  );
}

const CSS = `
.rdz{display:flex;flex-direction:column;gap:8px;padding:10px;margin:0 0 12px;border:2px solid #965600;border-radius:12px;background:#fffaf1;color:#17232a;font-size:15px;line-height:1.45}
.rdz *{box-sizing:border-box}
.rdz-tete{display:flex;align-items:center;gap:8px;flex-wrap:wrap}
.rdz-pastille{background:#ef4444;color:#fff;border-radius:999px;min-width:22px;height:22px;padding:0 7px;display:inline-flex;align-items:center;justify-content:center;font-size:.78rem;font-weight:800}
.rdz-carte{display:flex;flex-direction:column;gap:4px;padding:8px 10px;border:1px solid #cfd8dc;border-radius:10px;background:#fff;min-width:0;overflow-wrap:anywhere}
.rdz-carte.fait{opacity:.8}
.rdz-ref{font-weight:700}
.rdz-nouveau{margin-left:8px;background:#ef4444;color:#fff;border-radius:6px;padding:0 6px;font-size:.72rem;font-weight:800;text-transform:uppercase}
.rdz-note{font-size:.86rem;color:#5b6b73}
.rdz-note.err{color:#a6342b}.rdz-note.ok{color:#2a7034;font-weight:600}
.rdz-instr{white-space:pre-wrap}
.rdz-mono{font-family:"JetBrains Mono",Consolas,monospace;font-size:.88rem}
.rdz-imprimante{font-weight:700;color:#0f6e74}
.rdz-actions{display:flex;gap:8px;flex-wrap:wrap;align-items:center;margin-top:4px}
.rdz-btn{font:inherit;font-weight:700;border-radius:10px;border:1px solid #cfd8dc;background:#fff;color:#17232a;padding:8px 12px;min-height:44px;cursor:pointer;text-decoration:none;display:inline-flex;align-items:center;justify-content:center;gap:4px}
.rdz-btn.primaire{background:#0f6e74;border-color:#0f6e74;color:#fff}
.rdz-btn.discret{font-weight:600;min-height:40px;padding:6px 10px}
.rdz-btn:disabled{opacity:.45;cursor:not-allowed}
.rdz-btn:focus-visible{outline:3px solid #0f6e74;outline-offset:2px}
.rdz-histo summary{cursor:pointer;font-size:.88rem;color:#5b6b73}
.rdz-tout{display:flex;gap:8px;flex-wrap:wrap;align-items:center}
.rdz-ligne{display:flex;align-items:center;gap:12px;flex-wrap:wrap;padding:8px 10px;border:1px solid #cfd8dc;border-radius:10px;background:#fff;min-width:0;overflow-wrap:anywhere}
.rdz-ligne.fait{opacity:.8}
.rdz-ligne-texte{flex:1 1 260px;min-width:0;display:flex;flex-direction:column;gap:2px}
.rdz-etat{font-size:.9rem;font-weight:700;border-radius:8px;padding:4px 8px;overflow-wrap:anywhere}
.rdz-etat.attente{background:#e3f0f1;color:#0f6e74}.rdz-etat.err{background:#f8dedb;color:#a6342b}
.rdz-msg{padding:8px 10px;border-radius:8px;font-size:.9rem;background:#dcefdc;color:#2a7034;overflow-wrap:anywhere}
.rdz-msg.err{background:#f8dedb;color:#a6342b}.rdz-msg.warn{background:#fbecd3;color:#965600}
`;
